import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import calendarService from '../../../services/calendarService';
import { format } from 'date-fns';

const EventModal = ({ event, onSave, onCancel, onClose, onDelete, userId, isOpen }) => {
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    event_type: 'call',
    start_time: '',
    end_time: '',
    timezone: 'UTC',
    location: '',
    virtual_meeting_url: '',
    status: 'scheduled',
    priority: 'medium',
    all_day: false,
    recurring: false,
    metadata: null,
    contact_id: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (event) {
      setEventData({
        title: event?.title || '',
        description: event?.description || '',
        event_type: event?.event_type || 'call',
        start_time: event?.start_time ? format(new Date(event?.start_time), "yyyy-MM-dd'T'HH:mm") : '',
        end_time: event?.end_time ? format(new Date(event?.end_time), "yyyy-MM-dd'T'HH:mm") : '',
        timezone: event?.timezone || 'UTC',
        location: event?.location || '',
        virtual_meeting_url: event?.virtual_meeting_url || '',
        status: event?.status || 'scheduled',
        priority: event?.priority || 'medium',
        all_day: event?.all_day || false,
        recurring: event?.recurring || false,
        metadata: event?.metadata || null,
        contact_id: event?.contact_id || null
      });
    } else {
      // Set default start time to next hour if creating new event
      const now = new Date();
      const nextHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours() + 1, 0);
      const endTime = new Date(nextHour.getTime() + 30 * 60000); // 30 minutes later

      setEventData(prev => ({
        ...prev,
        start_time: format(nextHour, "yyyy-MM-dd'T'HH:mm"),
        end_time: format(endTime, "yyyy-MM-dd'T'HH:mm")
      }));
    }

    loadContacts();
  }, [event]);

  const loadContacts = async () => {
    // This would typically load from communication_contacts table
    // For now, we'll use a simplified approach
    try {
      // In a real implementation, you'd fetch contacts from the contacts service
      setContacts([]);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setEventData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-update end time when start time changes
    if (field === 'start_time' && value) {
      const startTime = new Date(value);
      const endTime = new Date(startTime.getTime() + 30 * 60000); // 30 minutes later
      setEventData(prev => ({
        ...prev,
        end_time: format(endTime, "yyyy-MM-dd'T'HH:mm")
      }));
    }
  };

  const handleSave = async () => {
    if (!eventData?.title?.trim()) {
      setError('Event title is required');
      return;
    }

    if (!eventData?.start_time || !eventData?.end_time) {
      setError('Start and end times are required');
      return;
    }

    if (new Date(eventData?.start_time) >= new Date(eventData?.end_time)) {
      setError('End time must be after start time');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const eventPayload = {
        ...eventData,
        user_id: userId,
        start_time: new Date(eventData?.start_time)?.toISOString(),
        end_time: new Date(eventData?.end_time)?.toISOString()
      };

      let result;
      if (event?.id) {
        result = await calendarService?.updateCalendarEvent(event?.id, eventPayload);
      } else {
        result = await calendarService?.createCalendarEvent(eventPayload);
      }

      if (result?.error) {
        setError(result?.error);
        return;
      }

      onSave?.();
    } catch (err) {
      setError('Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const closeFn = onCancel || onClose;
    closeFn?.();
  };

  const handleDelete = async () => {
    if (!event?.id) return;

    if (!confirm('Are you sure you want to delete this event?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await calendarService?.deleteCalendarEvent(event?.id);
      
      if (deleteError) {
        setError(deleteError);
        return;
      }

      // Call onDelete if provided, otherwise onSave
      if (onDelete) {
        onDelete?.();
      } else {
        onSave?.();
      }
    } catch (err) {
      setError('Failed to delete event');
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = [
    { value: 'call', label: 'Call', icon: 'Phone' },
    { value: 'meeting', label: 'Meeting', icon: 'Users' },
    { value: 'follow_up', label: 'Follow-up', icon: 'ArrowRight' },
    { value: 'reminder', label: 'Reminder', icon: 'Bell' },
    { value: 'deadline', label: 'Deadline', icon: 'Clock' }
  ];

  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no_show', label: 'No Show' }
  ];

  const reminderOptions = [
    { value: 0, label: 'No reminder' },
    { value: 5, label: '5 minutes before' },
    { value: 15, label: '15 minutes before' },
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 1440, label: '1 day before' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {event ? 'Edit Event' : 'Schedule New Event'}
          </h2>
          <div className="flex items-center space-x-2">
            {event?.id && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={loading}
                iconName="Trash2"
                className="text-destructive hover:text-destructive"
              >
                Delete
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleClose}
              iconName="X"
            />
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <span className="text-destructive text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Event Title *
              </label>
              <Input
                type="text"
                value={eventData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                placeholder="Enter event title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Event Type
              </label>
              <select
                value={eventData?.event_type}
                onChange={(e) => handleInputChange('event_type', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                {eventTypes?.map(type => (
                  <option key={type?.value} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Priority
              </label>
              <select
                value={eventData?.priority}
                onChange={(e) => handleInputChange('priority', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Start Time *
              </label>
              <Input
                type="datetime-local"
                value={eventData?.start_time}
                onChange={(e) => handleInputChange('start_time', e?.target?.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                End Time *
              </label>
              <Input
                type="datetime-local"
                value={eventData?.end_time}
                onChange={(e) => handleInputChange('end_time', e?.target?.value)}
                required
              />
            </div>
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="all-day"
              checked={eventData?.all_day}
              onChange={(e) => handleInputChange('all_day', e?.target?.checked)}
              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
            />
            <label htmlFor="all-day" className="text-sm font-medium text-foreground">
              All day event
            </label>
          </div>

          {/* Location and Meeting Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Location
              </label>
              <Input
                type="text"
                value={eventData?.location}
                onChange={(e) => handleInputChange('location', e?.target?.value)}
                placeholder="Enter location or address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Meeting URL
              </label>
              <Input
                type="url"
                value={eventData?.virtual_meeting_url}
                onChange={(e) => handleInputChange('virtual_meeting_url', e?.target?.value)}
                placeholder="https://zoom.us/j/..."
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Status
            </label>
            <select
              value={eventData?.status}
              onChange={(e) => handleInputChange('status', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              {statusOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description/Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description & Notes
            </label>
            <textarea
              value={eventData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              placeholder="Enter event description, agenda, preparation notes, or other details..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use this field for event details, meeting agenda, preparation notes, follow-up actions, or any other relevant information.
            </p>
          </div>

          {/* Recurring Event Toggle */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="recurring"
              checked={eventData?.recurring}
              onChange={(e) => handleInputChange('recurring', e?.target?.checked)}
              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
            />
            <label htmlFor="recurring" className="text-sm font-medium text-foreground">
              Recurring event
            </label>
            {eventData?.recurring && (
              <span className="text-xs text-muted-foreground">
                (Advanced recurrence options coming soon)
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !eventData?.title?.trim()}
            iconName={loading ? "Loader2" : "Save"}
            iconPosition="left"
            className={loading ? "animate-spin" : ""}
          >
            {loading ? 'Saving...' : (event ? 'Update Event' : 'Schedule Event')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventModal;